// The parts of a signup record (owner, November 3: a signup is a tenant):
// shown on the tenant page's Signup tab while the league exists, and on the
// deleted tenant's page (/tenants/signups/:id) once it's gone. Who signed up,
// where they came from, what their work address's domain says about their
// organisation, the cleanup and its Keep switch, the Growth lead, and notes
// with a timeline of every email and reply.

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '@/api'
import type { OrganisationLookup, SignupProfile, SignupTimelineItem } from '@/api/growthSignups'
import type { LeadStatus } from '@/api/types'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorState,
  ExternalLinkIcon,
  KeyValueList,
  OnboardingSteps,
  RelativeTime,
  Textarea,
  ToggleRow,
  buttonClasses,
  useToast,
} from '@/components/ui'
import { auditHeadline } from '@/lib/audit'
import { formatDate, formatDateTime, titleCase } from '@/lib/format'
import { invalidateQueries, useQuery } from '@/lib/query'
import { deletionLabel, EMAIL_STATUS_LABELS, EMAIL_STATUS_TONE, lookupLinks, STATE_TONE, stateLabel } from '@/lib/signups'
import { LeadStatusBadge, SafeExternalLink } from '../growth/shared'
import { TouchesView } from '../growth/signupShared'

export function Person({ signup, title = 'Person', withLeague = true }: { signup: SignupProfile; title?: string; withLeague?: boolean }) {
  const l = signup.league
  const links = lookupLinks(signup.person.name, l.name)
  return (
    <Card>
      <CardHeader title={title} />
      <KeyValueList
        items={[
          { label: 'Name', value: signup.person.name ?? <span className="text-muted">Not given</span> },
          { label: 'Email', value: signup.person.email ? <span className="break-all font-mono text-xs">{signup.person.email}</span> : <span className="text-muted">None</span> },
          ...(withLeague
            ? [
                { label: 'League', value: l.name },
                { label: 'Sport', value: l.sport ? titleCase(l.sport) : '—' },
                { label: 'Site', value: l.site_url ? <SafeExternalLink href={l.site_url} /> : <span className="break-all text-muted">{l.subdomain} (deleted)</span> },
                ...(l.template ? [{ label: 'Template', value: l.template.name }] : []),
              ]
            : []),
          { label: 'Signed up', value: formatDateTime(signup.signed_up_at) },
          { label: 'Last login', value: signup.person.last_login_at ? <RelativeTime value={signup.person.last_login_at} /> : <span className="text-muted">Never</span> },
        ]}
      />
      {links.length ? (
        <div className="mt-4 border-t border-line pt-3">
          <p className="mb-2 text-2xs font-bold uppercase tracking-eyebrow text-muted">Look them up</p>
          <div className="flex flex-wrap gap-2" aria-label="Look them up">
            {links.map((x) => (
              <a key={x.label} href={x.href} target="_blank" rel="noopener noreferrer" className={buttonClasses({ size: 'sm' })}>
                <ExternalLinkIcon size={13} />
                {x.label}
              </a>
            ))}
          </div>
          <p className="mt-2 text-xs text-subtle">Each opens a search in a new tab. Nothing is read from those sites.</p>
        </div>
      ) : null}
    </Card>
  )
}

export function Organisation({ signup }: { signup: SignupProfile }) {
  const personal = !signup.email_domain || signup.email_domain.personal
  const org = useQuery(personal ? null : ['signups', 'organisation', signup.id], (signal) => api.growth.signups.organisation(signup.id, signal))
  if (personal) return null
  const o: OrganisationLookup | undefined = org.data
  return (
    <Card>
      <CardHeader title="Organisation" description={`From ${signup.email_domain!.domain}, their email's domain.`} />
      {org.status === 'loading' ? (
        <p className="text-[13px] text-muted" role="status">Reading their home page…</p>
      ) : org.status === 'error' ? (
        <ErrorState error={org.error} title="Couldn't look it up" onRetry={org.refetch} className="py-4" />
      ) : o?.status === 'ok' ? (
        <div className="grid gap-1.5">
          <p className="break-words text-[13.5px] font-semibold text-ink">{o.title ?? o.domain}</p>
          {o.description ? <p className="break-words text-[13px] text-muted">{o.description}</p> : null}
          {o.url ? <SafeExternalLink href={o.url} /> : null}
        </div>
      ) : (
        <p className="text-[13px] text-muted">
          Couldn't read {o?.domain ?? 'their site'}{o?.outcome ? ` (${o.outcome.replace(/_/g, ' ')})` : ''}.{' '}
          {o?.url ? <SafeExternalLink href={o.url} /> : null}
        </p>
      )}
    </Card>
  )
}

export function WhatTheyDid({ signup }: { signup: SignupProfile }) {
  const id = signup.league.id!
  const usage = useQuery(['tenant', id, 'usage'], (signal) => api.tenants.usage(id, signal))
  const recent = useQuery(['tenant', id, 'recent'], (signal) => api.audit.list({ league_id: id, limit: 5 }, signal))
  const logins = usage.data?.login_history.filter((l) => l.success) ?? []
  return (
    <Card>
      <CardHeader
        title="What they've done"
        actions={
          <Link to={`/tenants/${id}/overview`} className="text-xs font-semibold text-accent hover:underline">
            Tenant page
          </Link>
        }
      />
      <div className="grid gap-4">
        <div>
          <p className="mb-2 text-2xs font-bold uppercase tracking-eyebrow text-muted">Setup progress</p>
          {usage.data ? <OnboardingSteps state={usage.data.onboarding} /> : usage.status === 'error' ? <p className="text-xs text-muted">Couldn't load setup progress.</p> : <div className="h-8 animate-pulse rounded bg-hover/60" role="status" aria-label="Loading setup progress" />}
        </div>
        <KeyValueList
          items={[
            { label: 'Logins', value: usage.data ? `${logins.length}${logins.length >= 20 ? '+' : ''} recorded` : '…' },
            { label: 'Last login', value: logins[0] ? <RelativeTime value={logins[0].created_at} /> : <span className="text-muted">None</span> },
            { label: 'Last activity', value: signup.last_activity_at ? <RelativeTime value={signup.last_activity_at} /> : <span className="text-muted">None yet</span> },
            ...(usage.data ? [{ label: 'Content', value: `${usage.data.content_inventory.teams} teams, ${usage.data.content_inventory.games} games` }] : []),
          ]}
        />
        <div>
          <p className="mb-2 text-2xs font-bold uppercase tracking-eyebrow text-muted">Recent changes</p>
          {recent.data?.entries.length ? (
            <ul className="grid gap-1.5">
              {recent.data.entries.map((e) => (
                <li key={e.id} className="flex flex-wrap items-baseline gap-x-2 text-[13px]">
                  <span className="min-w-0 break-words text-ink">{auditHeadline(e)}</span>
                  <RelativeTime value={e.created_at} className="text-xs text-muted" />
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[13px] text-muted">{recent.status === 'loading' ? 'Loading…' : 'Nothing yet.'}</p>
          )}
        </div>
      </div>
    </Card>
  )
}

export function Cleanup({ signup, onChange }: { signup: SignupProfile; onChange: (next: SignupProfile) => void }) {
  const l = signup.lifecycle
  const deleted = l.state === 'deleted'
  const paid = l.state === 'paid'
  const line = deletionLabel(l, formatDate)
  return (
    <Card>
      <CardHeader title="Cleanup" description="A league that never pays is deleted 30 days after its trial ends, with one warning to its owner a week before." />
      <div className="grid gap-3">
        <p className="flex flex-wrap items-center gap-2 text-[13.5px]">
          <Badge tone={STATE_TONE[l.state]}>{stateLabel(l)}</Badge>
          {line ? <span className={l.needs_look ? 'text-warn' : 'text-muted'}>{line}</span> : null}
        </p>
        {l.needs_look ? <p className="text-xs text-muted">Money moved through this league (tickets, a form or registration payment, or a connected Stripe account), so it's never deleted automatically.</p> : null}
        {l.deletion?.mode === 'would' ? <p className="text-xs text-muted">The cleanup is off (SIGNUP_CLEANUP_ENABLED isn't true), so nothing happens on that date.</p> : null}
        {l.deletion?.warned_at ? <p className="text-xs text-muted">The owner was warned on {formatDate(l.deletion.warned_at)}.</p> : null}
        {!deleted && !paid ? (
          <ToggleRow
            label="Keep this league"
            description="The cleanup never deletes it. You can turn this off again."
            checked={signup.keep}
            onToggle={async (next) => {
              const out = await api.growth.signups.setKeep(signup.id, next)
              onChange(out)
              invalidateQueries('signups')
            }}
          />
        ) : null}
      </div>
    </Card>
  )
}

export function LeadCard({ signup, onAdded }: { signup: SignupProfile; onAdded: () => void }) {
  const toast = useToast()
  const navigate = useNavigate()
  const [adding, setAdding] = useState(false)
  return (
    <Card>
      <CardHeader title="Growth lead" />
      {signup.lead ? (
        <p className="flex flex-wrap items-center gap-2 text-[13.5px]">
          <Link to={`/growth/leads/${signup.lead.id}`} className="font-semibold text-accent hover:underline">
            {signup.lead.name}
          </Link>
          <LeadStatusBadge status={signup.lead.status as LeadStatus} />
        </p>
      ) : (
        <div className="grid gap-2">
          <p className="text-[13px] text-muted">No lead on this address yet.</p>
          <div>
            <Button
              size="sm"
              loading={adding}
              disabled={!signup.person.email}
              onClick={async () => {
                setAdding(true)
                try {
                  const out = await api.growth.signups.addLead(signup.id)
                  toast.success(out.created ? 'Lead added.' : 'That address is already a lead.')
                  invalidateQueries('growth')
                  onAdded()
                  navigate(`/growth/leads/${out.lead.id}`)
                } finally {
                  setAdding(false)
                }
              }}
            >
              Add as lead
            </Button>
          </div>
        </div>
      )}
    </Card>
  )
}

/** Why a follow-up after signup didn't go, from the server's last look (outreach.ts › LAST_LOOK_WORDS). */
const FOLLOW_UP_STOPS: Record<string, string> = {
  follow_up_off: 'Not sent: the follow-up was turned off.',
  league_deleted: 'Not sent: their league was deleted.',
  in_conversation: "Not sent: we were already writing to each other.",
  suppressed: 'Not sent: the address is on the do-not-contact list.',
  missed: "Not sent: the server was down at its time.",
}

function TimelineEntry({ item }: { item: SignupTimelineItem }) {
  switch (item.kind) {
    case 'signed_up':
      return <>Signed up</>
    case 'league_deleted':
      return <>League deleted{item.by === 'cleanup' ? ' by the signup cleanup' : item.by === 'ops' ? ' from Ops' : ''}</>
    case 'deletion_warned':
      return <>Owner warned the league will be deleted{item.delete_on ? ` on ${formatDate(item.delete_on)}` : ''}</>
    case 'note':
      return (
        <span className="grid gap-0.5">
          <span className="whitespace-pre-wrap break-words text-ink">{item.body}</span>
          <span className="text-xs text-muted">Note by {item.actor?.name ?? item.actor?.email ?? 'someone'}</span>
        </span>
      )
    case 'email':
      return (
        <span className="grid gap-0.5">
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={EMAIL_STATUS_TONE[item.status]}>{EMAIL_STATUS_LABELS[item.status]}</Badge>
            {item.automatic === 'signup_follow_up' ? <Badge tone="neutral">Follow-up after signup</Badge> : null}
            <span className="break-words text-ink">{item.subject}</span>
          </span>
          <span className="text-xs text-muted">
            Signed by {item.signer_name}
            {item.actor ? `, sent by ${item.actor.name ?? item.actor.email}` : ''}
            {item.status === 'scheduled' && item.scheduled_for ? `. Goes ${formatDateTime(item.scheduled_for)}` : ''}
            {item.status === 'dry_run' ? '. Sending was off: nothing left.' : item.error ? (FOLLOW_UP_STOPS[item.error] ? `. ${FOLLOW_UP_STOPS[item.error]}` : ` (${item.error})`) : ''}
          </span>
        </span>
      )
    case 'reply':
    case 'stop':
      return (
        <span className="grid gap-0.5">
          <span className="text-ink">{item.kind === 'stop' ? 'Replied asking to stop. The address is on the do-not-contact list.' : 'Replied'}</span>
          {item.snippet ? <span className="whitespace-pre-wrap break-words text-xs text-muted">“{item.snippet}”</span> : null}
        </span>
      )
    case 'bounce':
      return <>Bounced{item.bounce_kind === 'hard' ? ': the address is on the do-not-contact list' : ' (for now)'}</>
  }
}

export function Timeline({ signup, onNote }: { signup: SignupProfile; onNote: () => void }) {
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  return (
    <Card>
      <CardHeader title="Timeline" description="Notes, every email and every reply. Only Ops sees notes." />
      <form
        className="mb-4 grid gap-2"
        onSubmit={async (e) => {
          e.preventDefault()
          if (!note.trim()) return
          setSaving(true)
          try {
            await api.growth.signups.addNote(signup.id, note)
            setNote('')
            onNote()
          } finally {
            setSaving(false)
          }
        }}
      >
        <Textarea aria-label="Add a note" placeholder="Add a note" value={note} rows={2} maxLength={5000} onChange={(e) => setNote(e.target.value)} />
        <div className="flex justify-end">
          <Button type="submit" size="sm" loading={saving} disabled={!note.trim()}>
            Add note
          </Button>
        </div>
      </form>
      <ol className="grid gap-3 border-t border-line pt-3" aria-label="Timeline">
        {signup.timeline.map((item, i) => (
          <li key={`${item.kind}-${i}`} className="grid gap-0.5 text-[13px]">
            <TimelineEntry item={item} />
            <span className="text-xs text-subtle">{formatDateTime(item.at)}</span>
          </li>
        ))}
      </ol>
    </Card>
  )
}

/** Where they came from, as the landing page reported it. */
export function WhereFrom({ signup }: { signup: SignupProfile }) {
  const a = signup.attribution
  const ids = a ? (['gclid', 'fbclid', 'msclkid', 'ga_client_id'] as const).filter((k) => a.click_ids[k]) : []
  return (
    <Card>
      <CardHeader title="Where they came from" description="As the landing page reported it." />
      {a ? (
        <TouchesView
          first={a.first_touch}
          last={a.last_touch}
          extra={[
            ...(a.country ? [{ label: 'Country', value: a.country }] : []),
            ...(ids.length ? [{ label: 'Ad click ids', value: <span className="break-all font-mono text-xs">{ids.map((k) => `${k}: ${a.click_ids[k]}`).join(', ')}</span> }] : []),
          ]}
        />
      ) : (
        <p className="text-[13px] text-muted">Nothing was recorded for this signup.</p>
      )}
    </Card>
  )
}
